// ---------------------------------------------------------------------------
// Google tag (gtag.js) for Google Ads, loaded ONLY after consent.
//
// History: the loader used to sit statically in index.html (with Consent Mode
// defaulting to denied) so Google's tag verifier could see it. That meant every
// visitor's browser contacted Google before being asked anything, which is
// what launch-readiness scanners flagged. Since 2026-09-26 this module injects
// gtag.js itself, and only once the visitor has tapped Accept. The trade-off:
// Google Ads' own tag checker (which never accepts) may call the tag
// unverified. Conversions from visitors who accept still record.
//
// Runtime:
//   - On Accept (or a returning visitor who already accepted): inject gtag.js,
//     set Consent Mode to granted, and `config` the account (live host only).
//   - On withdrawal: push `consent: update` denied (code already run can't be
//     un-run; this stops cookie-based measurement from then on).
//   - SPA page_views on route changes, and conversions at Lead / Sign up, only
//     while the tag is present.
//
// Dev/preview safety: the tag is only ever injected on entredash.com, so on
// dev/preview every call is a console-logging no-op. The live Ads account
// never hears from dev traffic (its conversion action is "Page view").
//
// The tag only ever sees marketing signals: which page was viewed, and that a
// Lead / Sign up happened. Never idea, memo or session content.
// ---------------------------------------------------------------------------

import { isMarketingGranted, subscribeConsent } from './consentManager';

const IS_PROD = import.meta.env.PROD;
const GOOGLE_ADS_ID = 'AW-18374288307';
let injected = false;

/** Inject gtag.js and configure the account. Live host only, once. */
function injectTag(): void {
  if (injected || !isLiveHost()) return;
  injected = true;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer!.push(arguments); };
  window.gtag('consent', 'default', consentSignals(true));
  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_ADS_ID);
  const el = document.createElement('script');
  el.async = true;
  el.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(el);
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * The four Consent Mode v2 storage signals, all set to the same value. `granted`
 * enables cookie-based ad + analytics measurement; `denied` keeps gtag in
 * cookieless-ping mode.
 */
function consentSignals(granted: boolean): Record<string, 'granted' | 'denied'> {
  const v = granted ? 'granted' : 'denied';
  return {
    ad_storage: v,
    ad_user_data: v,
    ad_personalization: v,
    analytics_storage: v,
  };
}

/** The live customer hosts. The tag is only ever injected on these. */
const LIVE_HOSTS = new Set(['entredash.com', 'www.entredash.com']);

function isLiveHost(): boolean {
  return typeof window !== 'undefined' && LIVE_HOSTS.has(window.location.hostname);
}

/** Safe to talk to the real tag: on the live host AND the loader is present. */
function tagPresent(): boolean {
  return isLiveHost() && injected && isMarketingGranted() && typeof window.gtag === 'function';
}

/**
 * Record a page_view on SPA route changes. gtag's `config` sends the first
 * page_view when the tag is injected; single-page navigations need an explicit
 * one. Only fires once the visitor has accepted and the tag is loaded.
 */
export function trackGooglePageView(): void {
  if (!tagPresent()) {
    if (!IS_PROD) console.info('[google-ads] (dev) page_view', window.location.pathname);
    return;
  }
  // Send the FULL page_location (href), not just page_path. Google Ads
  // URL-based "Page load" conversions (e.g. entredash.com/signup, /spark, /try)
  // match on page_location. In an SPA, gtag caches the page_location from the
  // initial config, so a client-side route change that only updates page_path
  // leaves gtag reporting the original landing URL — and those page-load
  // conversions never fire on in-app navigations. Passing page_location keeps
  // the reported URL in sync with the route on every navigation.
  window.gtag!('event', 'page_view', {
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
  });
}

/**
 * Fire a Google Ads conversion. `sendTo` is the full `AW-XXXX/label` string from
 * the Google Ads conversion action. Only fires once the visitor has accepted
 * and the tag is loaded; nothing is sent otherwise.
 *
 * `value` / `currency` are optional and only sent together (a currency with no
 * value carries no information).
 */
export function trackGoogleConversion(
  sendTo: string,
  params?: { value?: number; currency?: string; transactionId?: string },
): void {
  const payload: Record<string, unknown> = { send_to: sendTo };
  if (typeof params?.value === 'number' && params.value > 0) {
    payload.value = params.value;
    payload.currency = (params.currency ?? 'USD').toUpperCase();
  }
  if (params?.transactionId) payload.transaction_id = params.transactionId;

  if (!tagPresent()) {
    if (!IS_PROD) console.info('[google-ads] (dev) conversion', payload);
    return;
  }
  window.gtag!('event', 'conversion', payload);
}

/**
 * Install the tag lifecycle (called once, after the first interaction). Loads
 * the tag when consent is granted and follows later changes.
 */
export function installGoogleAdsLifecycle(): () => void {
  const onConsent = () => {
    const granted = isMarketingGranted();
    if (!isLiveHost()) {
      if (!IS_PROD) console.info('[google-ads] (dev) consent ->', granted ? 'granted' : 'denied');
      return;
    }
    if (granted) {
      if (!injected) injectTag();
      else window.gtag?.('consent', 'update', consentSignals(true));
    } else if (injected) {
      window.gtag?.('consent', 'update', consentSignals(false));
    }
  };

  onConsent(); // a returning visitor who already accepted loads the tag now
  return subscribeConsent(onConsent);
}
